// agentModalForm.js — E4-WS1-T01 (refactor-deepening / F4, AgentModal engine).
//
// Deep module: the PURE form/model/schedule logic that lived inline in the 1406-LOC
// AgentModal god-component — model-suggestion derivation, adapter/model mismatch +
// auto-correction, the sources↔system_prompt (de)serialization, and the agent + schedule
// payload builders (incl. cron). The modal keeps the React state/effects and delegates
// every derivation and payload build here, so the logic is testable without rendering.
//
// Deletion test (01-DOCTRINE): delete this and the registry-priority model list, the
// provider-prefix mismatch rules, the auto-correct-on-adapter-switch, the sources marker
// serialization, and the local/remote schedule payload scatter back into the component.
// Concentrated agent-form domain logic behind small functions → DEEP.
//
// PURE: no React, no window (Intl timezone is the one ambient read, matching the inline).

import { MODEL_REGISTRY } from '../../lib/modelRegistry.js';
import { isLmStudioAdapter } from '../../lib/localTaskRun.js';

// Common local Ollama models NOT in the static registry — datalist shortcuts only.
export const OLLAMA_COMMON_MODELS = [
  'qwen2.5:0.5b', 'qwen2.5:1.5b', 'qwen2.5:3b', 'qwen2.5:7b', 'qwen2.5:14b', 'qwen2.5:32b', 'qwen2.5:72b',
  'qwen2.5-coder:0.5b', 'qwen2.5-coder:1.5b', 'qwen2.5-coder:3b', 'qwen2.5-coder:7b', 'qwen2.5-coder:14b', 'qwen2.5-coder:32b',
  'qwen2.5-math:1.5b', 'qwen2.5-math:7b', 'qwen2.5-math:72b',
  'qwen3:8b',
  'llama3.2', 'llama3.2:1b', 'llama3.2:3b', 'llama3.3:70b',
  'mistral-small:24b', 'gemma2:9b', 'gemma2:27b', 'gemma3:4b', 'gemma3:12b', 'gemma3:27b',
  'deepseek-r1:7b', 'deepseek-r1:14b', 'deepseek-r1:32b', 'phi4:14b',
];

const KNOWN_PROVIDERS = ['anthropic', 'openai', 'google', 'deepseek', 'openrouter', 'xai'];

// ——— cron builders (forward). The reverse `parseCron` stays in the component (form init). ———
export function buildCron(freq, time) {
  const [h, m] = (time || '09:00').split(':').map(Number);
  switch (freq) {
    case 'weekdays': return `${m} ${h} * * 1-5`;
    case 'weekly':   return `${m} ${h} * * 1`;
    case 'monthly':  return `${m} ${h} 1 * *`;
    case 'every_6h': return `${m} */6 * * *`;
    default:         return `${m} ${h} * * *`;
  }
}
export function buildCronDatetime(dt) {
  if (!dt) return '0 9 * * *';
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? '0 9 * * *' : `${d.getMinutes()} ${d.getHours()} ${d.getDate()} ${d.getMonth() + 1} *`;
}

// ——— adapter / model derivations ———
export function isOllamaAdapter(adapter) {
  if (!adapter) return false;
  const slug = (adapter.name || '').toLowerCase();
  const display = (adapter.display_name || '').toLowerCase();
  return slug.includes('ollama') || display.includes('ollama');
}

// The datalist model suggestions, by priority: Ollama-local (or registry+common when
// offline) → adapter.available_models → registry filtered by provider → full registry.
export function computeModelSuggestions({ selectedAdapter, isOllama, ollamaLocalModels }) {
  if (isOllama) {
    const local = (ollamaLocalModels || []).map((m) => m.name);
    if (local.length) return local;
    const registryOllama = MODEL_REGISTRY.filter((m) => m.provider === 'ollama').map((m) => m.id);
    return Array.from(new Set([...registryOllama, ...OLLAMA_COMMON_MODELS]));
  }
  if (selectedAdapter?.available_models?.length) return selectedAdapter.available_models;
  if (selectedAdapter) {
    const slug = (selectedAdapter.name || '').toLowerCase();
    const matched = MODEL_REGISTRY.filter((m) => slug.includes(m.provider)).map((m) => m.id);
    if (matched.length) return matched;
  }
  return MODEL_REGISTRY.map((m) => m.id);
}

// Live-probed adapter models first, then the static suggestions (dedup, order preserved).
export function computeDropdownModels({ selectedAdapter, liveModels, modelSuggestions }) {
  const live = selectedAdapter ? (liveModels || []) : [];
  return Array.from(new Set([...live, ...modelSuggestions]));
}

// Does the current model NOT belong to the selected adapter (→ warning)?
export function computeModelMismatch({ selectedAdapter, isOllama, model }) {
  if (!selectedAdapter || !model) return false;
  const slug = (selectedAdapter.name || '').toLowerCase();
  if (isOllama) {
    // Ollama takes "ollama/xxx" or a bare "llama3.2" — mismatch only if the model is
    // explicitly prefixed with ANOTHER known provider.
    return KNOWN_PROVIDERS.some((p) => model.toLowerCase().startsWith(`${p}/`));
  }
  const m = model.toLowerCase();
  const mProvider = m.split('/')[0];
  if (!mProvider || mProvider === m) return false; // no prefix → ok
  return !slug.includes(mProvider);
}

// Switching adapters auto-corrects an obviously-mismatched model. Returns the next form.
export function autoCorrectModelOnAdapterChange(prevForm, nextAdapter) {
  const newForm = { ...prevForm, llm_adapter_id: nextAdapter?.id || null };
  if (!nextAdapter) return newForm;
  const slug = (nextAdapter.name || '').toLowerCase();
  const isOllamaNext = slug.includes('ollama');
  const m = prevForm.model.toLowerCase();
  const hasOtherProvider = KNOWN_PROVIDERS.some((p) => m.startsWith(`${p}/`));
  if (isOllamaNext && hasOtherProvider) {
    newForm.model = nextAdapter.default_model || 'qwen2.5:7b';
  } else if (nextAdapter.default_model) {
    const isMismatch = (() => {
      if (isOllamaNext) return hasOtherProvider;
      const mp = m.split('/')[0];
      if (!mp || mp === m) return false;
      return !slug.includes(mp);
    })();
    if (isMismatch) newForm.model = nextAdapter.default_model;
  }
  return newForm;
}

// ——— payload builders ———
const SOURCES_MARKER_RE = /\n*<!-- apolo:sources -->[\s\S]*?<!-- \/apolo:sources -->\n*/g;

// Serialize the sources list into the system prompt between invisible HTML markers
// (survives the current schema without a migration; ChatView extracts + pre-fetches them).
export function serializeSourcesIntoPrompt(rawSystemPrompt, sources) {
  let systemPrompt = (rawSystemPrompt || '').replace(SOURCES_MARKER_RE, '').trimEnd();
  if (sources.length > 0) {
    const urlsBlock = sources.map((s) => s.url).join('\n');
    systemPrompt = `${systemPrompt}\n\n<!-- apolo:sources -->\n${urlsBlock}\n<!-- /apolo:sources -->`;
  }
  return systemPrompt;
}

export function buildAgentPayload(form, systemPrompt) {
  return {
    name: form.name.trim(),
    emoji: form.emoji,
    system_prompt: systemPrompt,
    model: form.model,
    temperature: Number(form.temperature),
    max_tokens: Number(form.max_tokens),
    llm_adapter_id: form.llm_adapter_id || null,
  };
}

function resolveTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

// The scheduled-task cron + command payload. execution_target='local' for LOCAL adapters
// (Ollama OR LM Studio) — the remote Worker can't reach the user's localhost.
export function buildSchedulePayload({ form, sched, isOllama, selectedAdapter }) {
  const cron = sched.mode === 'once' ? buildCronDatetime(sched.oneTime) : buildCron(sched.frequency, sched.time);
  const payload = {
    prompt: form.system_prompt.trim(),
    model_id: form.model,
    llm_adapter_id: form.llm_adapter_id || null,
    output_destination: sched.outputDest,
    execution_target: (isOllama || isLmStudioAdapter(selectedAdapter)) ? 'local' : 'remote',
    timezone: resolveTimezone(),
  };
  return { cron, payload };
}
